/**
 * What erasure leaves: exactly `{"redacted":true}`, as an object or as a text column's value. Everything
 * shown from evidence, signals, audit or history goes through here, so that nobody reads the raw shell.
 * Display only: exports and API responses keep the shell.
 *
 * An object that merely has a `redacted` key among others is ordinary data. The one other erased shape is a
 * shell from before the 2026-11-02 ruling, with a 64-hex digest, which reads as erased until the catch-up
 * strips it. The server recognises the same two (AttributeShells, ProductErasureRedactionService).
 */
export const ERASED_LABEL = 'Erased';

const DIGEST = /^[0-9a-f]{64}$/;

function isShellObject(v: unknown): boolean {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return false;
  const o = v as Record<string, unknown>;
  if (o.redacted !== true) return false;
  const keys = Object.keys(o);
  if (keys.length === 1) return true;
  return keys.length === 2 && typeof o.value_sha256 === 'string' && DIGEST.test(o.value_sha256);
}

export function isErased(v: unknown): boolean {
  if (typeof v !== 'string') return isShellObject(v);
  const text = v.trim();
  if (!text.startsWith('{') || !text.includes('redacted')) return false;
  try {
    return isShellObject(JSON.parse(text));
  } catch {
    return false;
  }
}

/** The value as it was, or the label when it is a shell. */
export function erasedOr<T>(v: T): T | string {
  return isErased(v) ? ERASED_LABEL : v;
}
